import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { Navigate, Outlet, useLocation } from "react-router";

import { onUnauthenticated } from "@/api/client";
import { sessionKey, useSession } from "@/api/queries/auth";
import { ErrorState, PageLoader } from "@/components/ui";

import { platformHome, platformSections } from "./navigation";

/** Requires an authenticated session; otherwise sends the user to sign in and back. */
export function RequireSession() {
  const session = useSession();
  const location = useLocation();
  const qc = useQueryClient();
  useEffect(() => onUnauthenticated(() => qc.invalidateQueries({ queryKey: sessionKey })), [qc]);

  if (session.isLoading) return <PageLoader label="Loading K-SHOP" />;
  if (session.isError) return <ErrorState error={session.error} onRetry={() => session.refetch()} />;
  if (!session.data?.authenticated) {
    const next = `${location.pathname}${location.search}`;
    return <Navigate to={`/login?next=${encodeURIComponent(next)}`} replace />;
  }
  return <Outlet />;
}

/** Decide where an authenticated user lands. Never guesses a tenant it cannot justify. */
export function RootRedirect() {
  const { data } = useSession();
  if (!data?.authenticated) return <Navigate to="/login" replace />;
  if (data.impersonation) return <Navigate to={`/t/${data.impersonation.tenant_slug}`} replace />;
  const internal = data.memberships.filter((m) => m.has_internal_access);
  if (data.last_tenant_slug && internal.some((m) => m.tenant.slug === data.last_tenant_slug)) {
    return <Navigate to={`/t/${data.last_tenant_slug}`} replace />;
  }
  // One company is the only real choice; Kasten staff reach the platform from the workspace switcher.
  if (internal.length === 1) return <Navigate to={`/t/${internal[0].tenant.slug}`} replace />;
  if (internal.length === 0 && platformSections(data.platform.capabilities).length > 0) {
    return <Navigate to={platformHome(data.platform.capabilities)} replace />;
  }
  if (internal.length === 0) return <Navigate to="/no-access" replace />;
  return <Navigate to="/workspaces" replace />;
}

/** `/platform` opens the first section the person may use (the profile when there is none). */
export function PlatformIndexRedirect() {
  const { data } = useSession();
  if (!data) return <PageLoader />;
  return <Navigate to={platformHome(data.platform.capabilities)} replace />;
}
